import { Button } from '@creditornot/cb-button';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';

import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';

import { Offer } from '../../SharedMenuEditorPage/types';
import { ConfirmationModal } from '../components/ImageGalleryAddToOffers/ConfirmationModal';
import { ImageGalleryAddToOffers } from '../components/ImageGalleryAddToOffers/ImageGalleryAddToOffers';
import { ImageGalleryHeader } from '../components/ImageGalleryHeader';
import { useImageGalleryContext } from '../contexts/ImageGalleryContext';
import { DraftUpdateParams, OfferDraftChanges } from '../useOfferDraftChanges';
import { useOffersMutation } from '../useOffersMutation';

type Props = {
  updateOfferDraftChanges: (offerId: string, update: DraftUpdateParams) => void;
  offerDraftChanges: OfferDraftChanges;
  offersWithDraftChanges: Offer[];
  resetDraftChanges: () => void;
};

export const AddToOffersPage = ({
  updateOfferDraftChanges,
  offerDraftChanges,
  offersWithDraftChanges,
  resetDraftChanges,
}: Props) => {
  const history = useHistory();
  const { menuEditorPage } = useListingManagerRoutes();
  const { selectedImages } = useImageGalleryContext();
  const [isConfirmDiscardChangesModalOpen, setIsConfirmDiscardChangesModalOpen] = useState(false);
  const goBackToMenuEditor = useCallback(() => {
    toast.dismiss();
    history.push(menuEditorPage());
  }, [history, menuEditorPage]);
  const { isLoading: isUpdatingOffers, mutate: updateOffers } = useOffersMutation();
  const changedOffersCount = useMemo(() => Object.keys(offerDraftChanges).length, [offerDraftChanges]);

  const onClose = useCallback(() => {
    resetDraftChanges();
    setIsConfirmDiscardChangesModalOpen(false);
    goBackToMenuEditor();
  }, [resetDraftChanges, setIsConfirmDiscardChangesModalOpen, goBackToMenuEditor]);

  const handleClose = () => {
    if (changedOffersCount > 0) {
      setIsConfirmDiscardChangesModalOpen(true);
      return;
    }
    onClose();
  };

  const updateChangedOffers = useCallback(() => {
    updateOffers({
      offerDraftChanges,
      onSuccess: () => {
        resetDraftChanges();
        onClose();
      },
    });
  }, [offerDraftChanges, resetDraftChanges, updateOffers, onClose]);

  return (
    <>
      <ImageGalleryAddToOffers
        onClose={onClose}
        updateOfferDraftChanges={updateOfferDraftChanges}
        offerDraftChanges={offerDraftChanges}
        offersWithDraftChanges={offersWithDraftChanges}
        selectedImages={selectedImages}
        headerContent={
          <ImageGalleryHeader onClose={handleClose} hasCloseButton>
            <Button
              variant="primary"
              size="tiny"
              disabled={isUpdatingOffers || changedOffersCount < 1}
              onClick={updateChangedOffers}
            >
              <FormattedMessage id="shared-menu.editor.image-gallery.add-to-items.save-changes" />
            </Button>
          </ImageGalleryHeader>
        }
      />
      <ConfirmationModal
        open={isConfirmDiscardChangesModalOpen}
        onCancel={() => setIsConfirmDiscardChangesModalOpen(false)}
        onConfirm={onClose}
      />
    </>
  );
};
